// DDT-4096 — dismiss the on-screen keyboard once a dropdown choice is picked.
//
// A dropdown-backed field leaves focus on its input after a selection, so on a
// phone the soft keyboard stays up, covers the rest of the form and has to be
// closed by hand before the user can carry on. Kevin asked for this as a form
// rule across the whole site rather than a per-form patch, so it is wired once
// here, off the DOM events the two dropdown libraries already dispatch:
//
//   * autoComplete.js fires `selection` on its input with `bubbles: true`, and
//     only closes the list afterwards — it never re-focuses — so a document-level
//     listener catches every instance (search flow, contact form, online
//     consultation, search-filters modal) and any added later, with no
//     per-instance wiring, and runs after the instance's own handler has written
//     the value and the hidden fields.
//   * Tom Select fires a bubbling `change` on the original <select>, and parks
//     the instance on `element.tomselect`, which is enough to reach its control.
//
// Google Places autocomplete has no DOM event to hook, so its two call sites
// (the free-trial page and the divorce inline form, neither of them in this
// bundle) call `window.jfDismissKeyboard()` from their own `place_changed`
// handler instead of repeating the touch guard.
//
// Two deliberate limits, per the ticket's acceptance criteria:
//   * touch devices only. On desktop there is no keyboard to dismiss, and
//     blurring would fight keyboard navigation of the suggestion list.
//   * multi-select dropdowns are left alone — the user is expected to keep
//     picking. Tom Select multi-selects are excluded by their mode; an
//     autoComplete input can opt out with `data-keep-focus-on-select`.

const TOUCH_DEVICE_QUERY = '(hover: none) and (pointer: coarse)';

/**
 * True on devices that raise a soft keyboard for a focused text field.
 *
 * Evaluated per event rather than once at load: an iPad with a trackpad
 * attached reports a fine pointer and shows no keyboard, and that can change
 * within the life of the page.
 */
function hasSoftKeyboard() {
	return typeof window.matchMedia === 'function' &&
		window.matchMedia( TOUCH_DEVICE_QUERY ).matches;
}

/**
 * Drop focus from a field so the soft keyboard closes. No-op on desktop.
 *
 * Exposed as `window.jfDismissKeyboard` for the scripts that live outside this
 * bundle.
 *
 * @param {HTMLElement|null} element Field to blur.
 */
function dismissKeyboard( element ) {
	if ( ! element || typeof element.blur !== 'function' ) {
		return;
	}

	if ( ! hasSoftKeyboard() ) {
		return;
	}

	element.blur();
}

// autoComplete.js — one listener covers every instance on the site.
document.addEventListener( 'selection', function( event ) {
	const input = event.target;

	if ( ! ( input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement ) ) {
		return;
	}

	if ( input.hasAttribute( 'data-keep-focus-on-select' ) ) {
		return;
	}

	dismissKeyboard( input );
} );

// Tom Select — single-select only, and only while the control actually holds
// focus, so a programmatic setValue() during setup never steals it from
// whatever the user is typing in.
document.addEventListener( 'change', function( event ) {
	const select = event.target;
	const tomSelect = select && select.tomselect;

	if ( ! tomSelect || ! tomSelect.isFocused ) {
		return;
	}

	if ( tomSelect.settings.mode !== 'single' ) {
		return;
	}

	dismissKeyboard( tomSelect.focus_node || tomSelect.control_input );
} );

window.jfDismissKeyboard = dismissKeyboard;

export default dismissKeyboard;
